import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Modal from "components/Modal";
import type { AppDispatch } from "store";
import type {
  DrawingToolName,
  MainLineSettings,
  BackgroundSettings,
  FibonacciSettings,
} from "../model";
import {
  selectDrawingInstances,
  removeDrawingTool,
  saveDrawingToolSettings,
} from "../model";
import { MainLineSection } from "./MainLineSection";
import { BackgroundSection } from "./BackgroundSection";
import { FibonacciModal } from "./FibonacciModal";
import { TrashIcon } from "shared/ui";

import styles from "./styles/ToolSettingsModal.module.css";

const WITH_BACKGROUND: DrawingToolName[] = ["parallelChannel", "rectangle"];

interface Props {
  instanceId: string;
  toolName: DrawingToolName;
  toolLabel: string;
  onClose: () => void;
}

export const ToolSettingsModal: FC<Props> = ({
  instanceId,
  toolName,
  toolLabel,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const instances = useSelector(selectDrawingInstances);
  const instance = instances.find((i) => i.id === instanceId);

  const [mainLine, setMainLine] = useState<MainLineSettings>(
    instance?.mainLine ?? { color: "#22c55e", width: 1, opacity: 100 },
  );
  const [background, setBackground] = useState<BackgroundSettings>(
    instance?.background ?? { color: "#22c55e", opacity: 20 },
  );
  const [fibonacci, setFibonacci] = useState<FibonacciSettings>(
    instance?.fibonacci ?? {
      levelDisplay: "values",
      levelsColor: "#22c55e",
      levelsVisible: true,
      valuesColor: "#ffffff",
      valuesVisible: true,
    },
  );

  const isFibonacci = toolName === "fibonacciRetracement";
  const hasBackground = WITH_BACKGROUND.includes(toolName);

  const handleDelete = () => {
    dispatch(removeDrawingTool(instanceId));
    onClose();
  };

  const handleSave = () => {
    dispatch(
      saveDrawingToolSettings({
        id: instanceId,
        mainLine,
        fibonacci: isFibonacci ? fibonacci : undefined,
        background: hasBackground ? background : undefined,
      }),
    );
    onClose();
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={toolLabel}>
      <div className={styles.content}>
        {isFibonacci ? (
          <FibonacciModal
            mainLine={mainLine}
            fibonacci={fibonacci}
            onMainLineChange={setMainLine}
            onFibonacciChange={setFibonacci}
          />
        ) : (
          <>
            <MainLineSection value={mainLine} onChange={setMainLine} />
            {hasBackground && (
              <BackgroundSection value={background} onChange={setBackground} />
            )}
          </>
        )}
        <div className={styles.footer}>
          <button
            className={styles.deleteBtn}
            onClick={handleDelete}
            title="Delete"
          >
            <TrashIcon />
          </button>
          <button className={styles.saveBtn} onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
};
